import React, { useId } from 'react';
import { useMotorSound } from './useMotorSound';

export interface FanSvgProps {
  isRunning?: boolean;
  speed?: number; // 0 to 100 or RPM (adjusts rotation speed)
  direction?: 'cw' | 'ccw';
  isOverload?: boolean;
  color?: string; // Casing color (default: '#187AA0')
  bladeColor?: string;
  width?: string | number;
  height?: string | number;
  showLed?: boolean;
  label?: string | undefined;
  interactive?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent<SVGSVGElement>) => void;
  onPointerDown?: (e: React.PointerEvent<SVGSVGElement>) => void;
}

export const FanSvg: React.FC<FanSvgProps> = ({
  isRunning = false,
  speed = 100,
  direction = 'cw',
  isOverload = false,
  color = '#187AA0',
  bladeColor = '#1F242D',
  width = '100%',
  height = '100%',
  showLed = true,
  label,
  interactive = true,
  className = '',
  style,
  onClick,
  onPointerDown,
}) => {
  const uid = useId().replace(/:/g, '');
  const { isSpinning, isStopping } = useMotorSound(isRunning);
  const isRotating = isRunning || isSpinning;

  // Speed calculation: duration between 0.15s (max speed) and 1.2s (low speed)
  const normalizedSpeed = Math.max(10, Math.min(100, speed));
  const baseSpinDuration = 0.2 + (100 - normalizedSpeed) * 0.01;
  const spinDuration = (isStopping ? baseSpinDuration * 2.2 : baseSpinDuration).toFixed(2);

  // Casing base color
  const casingColor = isOverload ? '#b91c1c' : color;
  const casingDark = isOverload ? '#7f1d1d' : '#0e4e66';
  const casingLight = isOverload ? '#ef4444' : '#299ec9';

  const ledColor = isOverload ? '#ef4444' : isRunning ? '#22c55e' : (isStopping ? '#eab308' : '#4b5563');

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 100 100"
      width={width}
      height={height}
      className={`interactive-fan ${className}`}
      style={{
        overflow: 'visible',
        cursor: interactive ? 'pointer' : 'default',
        transition: 'transform 0.15s ease, filter 0.2s ease',
        ...style,
      }}
      onClick={onClick}
      onPointerDown={onPointerDown}
    >
      <defs>
        {/* Frame Gradient */}
        <linearGradient id={`fanFrame-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={casingLight} />
          <stop offset="50%" stopColor={casingColor} />
          <stop offset="100%" stopColor={casingDark} />
        </linearGradient>

        {/* Blade Metallic Gradient */}
        <radialGradient id={`fanBladeGrad-${uid}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(134, 140, 150, 1)" />
          <stop offset="70%" stopColor={bladeColor} />
          <stop offset="100%" stopColor="#93969cff" />
        </radialGradient>

        {/* Center Hub Gradient */}
        <radialGradient id={`fanHubGrad-${uid}`} cx="40%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#9ca3af" />
          <stop offset="45%" stopColor="#4b5563" />
          <stop offset="85%" stopColor="#1f2937" />
          <stop offset="100%" stopColor="#111827" />
        </radialGradient>

        {/* LED Glow Filter */}
        <filter id={`ledGlow-${uid}`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <style>{`
        @keyframes fan-spin-cw-${uid} {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes fan-spin-ccw-${uid} {
          from { transform: rotate(0deg); }
          to { transform: rotate(-360deg); }
        }
        @keyframes fan-vibration-${uid} {
          0% { transform: translate(0, 0); }
          25% { transform: translate(-0.25px, 0.25px); }
          50% { transform: translate(0.25px, -0.25px); }
          75% { transform: translate(-0.25px, -0.25px); }
          100% { transform: translate(0, 0); }
        }
        @keyframes fan-led-pulse-${uid} {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .fan-blade-spin-${uid} {
          transform-origin: 50px 50px;
          animation: ${direction === 'cw' ? `fan-spin-cw-${uid}` : `fan-spin-ccw-${uid}`} ${spinDuration}s linear infinite;
        }
        .fan-vibrating-${uid} {
          animation: fan-vibration-${uid} 0.08s linear infinite;
        }
        .fan-led-pulsing-${uid} {
          animation: fan-led-pulse-${uid} 0.8s ease-in-out infinite;
        }
      `}</style>

      {/* Main Container with Vibration when running */}
      <g className={isRotating ? `fan-vibrating-${uid}` : ''}>
        {/* Background Housing Cavity (Duct) */}
        <rect x="1" y="1" width="98" height="98" rx="12" fill="#0d1117" />
        <circle cx="50" cy="50" r="41.5" fill="#151922" stroke="#1f2937" strokeWidth="1" />

        {/* Inner Duct Depth rings */}
        <circle cx="50" cy="50" r="38" fill="none" stroke="#0f172a" strokeWidth="1.5" />
        <circle cx="50" cy="50" r="34" fill="none" stroke="#0b0f19" strokeWidth="0.8" />

        {/* Airflow Flow Guide Rings (visible when spinning) */}
        {isRotating && (
          <g opacity="0.35">
            <circle cx="50" cy="50" r="28" fill="none" stroke="#38bdf8" strokeWidth="1" strokeDasharray="16 8" className={`fan-blade-spin-${uid}`} />
            <circle cx="50" cy="50" r="36" fill="none" stroke="#7dd3fc" strokeWidth="0.8" strokeDasharray="12 12" className={`fan-blade-spin-${uid}`} />
          </g>
        )}

        {/* Rotating Fan Blades (Geometry from ac_single.svg) */}
        <g id="fan-blades" className={isRotating ? `fan-blade-spin-${uid}` : ''} style={{ transformOrigin: '50px 50px' }}>
          {/* Blade Shadow & Aerodynamic Wing */}
          <path
            fill={`url(#fanBladeGrad-${uid})`}
            stroke="#2e3846"
            strokeWidth="0.6"
            d="M80.471,44.18c2.698,0.899,6.704-0.613,7.603-7.807c0.899-7.194-12.999-23.504-24.69-23.504
              c-12.673,0-16.276,14.382-14.804,29.026c-1.632,0.143-3.146,0.697-4.436,1.56c-5.183-7.988-2.693-20.906-1.903-23.274
              c0.899-2.698-0.613-6.704-7.807-7.603c-7.194-0.899-23.504,12.999-23.504,24.69c0,12.673,14.383,16.277,29.027,14.804
              c0.126,1.656,0.673,3.193,1.54,4.503c-7.981,5.255-20.982,2.751-23.358,1.959c-2.698-0.899-6.704,0.613-7.603,7.807
              c-0.899,7.194,12.999,23.504,24.69,23.504c12.674,0,16.277-14.384,14.804-29.028c1.718-0.109,3.313-0.674,4.662-1.578
              c5.183,7.988,2.693,20.906,1.903,23.274c-0.899,2.698,0.613,6.704,7.807,7.603c7.194,0.899,23.504-12.999,23.504-24.69
              c0-12.673-14.383-16.277-29.027-14.804c-0.128-1.694-0.698-3.265-1.599-4.595C65.274,40.917,78.111,43.393,80.471,44.18z"
          />

          {/* Blade Center Mount Disk */}
          <circle cx="50" cy="50" r="14" fill="#1b212b" stroke="#333b47" strokeWidth="0.8" />
        </g>

        {/* Fan Housing & Protective Grille Frame (Geometry from ac_single.svg) */}
        <g id="fan-frame">
          <path
            fill={`url(#fanFrame-${uid})`}
            stroke="rgba(255, 255, 255, 0.15)"
            strokeWidth="0.5"
            d="M86.816,0H13.184C5.903,0,0,5.903,0,13.184v73.633C0,94.097,5.903,100,13.184,100h73.633
              C94.097,100,100,94.097,100,86.816V13.184C100,5.903,94.097,0,86.816,0z M77.821,19.071L53.719,43.173
              c-1.105-0.603-2.372-0.947-3.719-0.947c-1.348,0-2.614,0.344-3.719,0.947L22.179,19.071C29.55,12.437,39.302,8.396,50,8.396
              S70.45,12.437,77.821,19.071z M8.396,50c0-10.698,4.041-20.45,10.677-27.821l24.102,24.102c-0.603,1.105-0.947,2.371-0.947,3.719
              s0.343,2.614,0.947,3.719L19.072,77.821C12.437,70.45,8.396,60.698,8.396,50z M22.179,80.929l24.102-24.102
              c1.105,0.603,2.372,0.947,3.719,0.947s2.614-0.344,3.719-0.947l24.102,24.102C70.45,87.563,60.698,91.604,50,91.604
              S29.55,87.563,22.179,80.929z M80.928,77.821L56.827,53.719c0.603-1.105,0.947-2.372,0.947-3.719c0-1.348-0.344-2.614-0.947-3.719
              l24.102-24.102C87.563,29.549,91.604,39.302,91.604,50S87.563,70.451,80.928,77.821z"
          />

          {/* Corner Screw Mounting Holes */}
          <circle cx="7" cy="7" r="3.2" fill="#0d1117" stroke="#374151" strokeWidth="0.8" />
          <circle cx="93" cy="7" r="3.2" fill="#0d1117" stroke="#374151" strokeWidth="0.8" />
          <circle cx="7" cy="93" r="3.2" fill="#0d1117" stroke="#374151" strokeWidth="0.8" />
          <circle cx="93" cy="93" r="3.2" fill="#0d1117" stroke="#374151" strokeWidth="0.8" />
          <circle cx="7" cy="7" r="1.4" fill="#1f2937" />
          <circle cx="93" cy="7" r="1.4" fill="#1f2937" />
          <circle cx="7" cy="93" r="1.4" fill="#1f2937" />
          <circle cx="93" cy="93" r="1.4" fill="#1f2937" />
        </g>

        {/* Central Motor Hub Bearing & Motor Cap */}
        <g id="fan-hub">
          <circle cx="50" cy="50" r="10.5" fill={`url(#fanHubGrad-${uid})`} stroke="#111827" strokeWidth="1" />
          <circle cx="50" cy="50" r="7.5" fill="none" stroke="#6b7280" strokeWidth="0.6" strokeDasharray="3 1.5" />
          <circle cx="50" cy="50" r="4" fill="#374151" stroke="#9ca3af" strokeWidth="0.8" />
          <circle cx="49" cy="49" r="1.2" fill="#f3f4f6" opacity="0.8" />
        </g>

        {/* Status LED Indicator */}
        {showLed && (
          <g id="fan-led">
            <circle cx="86" cy="14" r="3.5" fill="#1f2937" stroke="#374151" strokeWidth="0.7" />
            <circle
              cx="86"
              cy="14"
              r="2.2"
              fill={ledColor}
              filter={isRunning || isOverload ? `url(#ledGlow-${uid})` : undefined}
              className={isOverload ? `fan-led-pulsing-${uid}` : ''}
            />
          </g>
        )}

        {/* Label Badge if present */}
        {label && (
          <g transform="translate(50, 93)" pointerEvents="none">
            <rect x="-24" y="-5" width="48" height="10" rx="3" fill="#000" opacity="0.75" />
            <text x="0" y="2.5" fill="#ffffff" fontSize="6.5" fontWeight="bold" textAnchor="middle">
              {label}
            </text>
          </g>
        )}
      </g>
    </svg>
  );
};
